import * as React from 'react'
import { cn } from '@/lib/utils'

export interface InputProps extends Omit<React.ComponentProps<'input'>, 'size'> {
	variant?: 'default' | 'icon'
	icon?: React.ReactNode
	containerClassName?: string
	error?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, type = 'text', variant = 'default', icon, containerClassName, error = false, disabled, onFocus, onBlur, ...props }, ref) => {
		const [isFocused, setIsFocused] = React.useState(false)
		const showIcon = variant === 'icon' && icon != null
		const isError = error || props['aria-invalid'] === true || props['aria-invalid'] === 'true'
		const isDisabled = Boolean(disabled)

		return (
			<div className={cn('group w-full min-w-0', containerClassName)}>
				<div
					className={cn(
						'rounded-xl border p-0.5 transition-colors',
						isDisabled ? 'border-border' : isError ? 'border-destructive' : isFocused ? 'border-primary' : 'border-border group-hover:border-white/10',
					)}
				>
					<div className={cn('flex items-center gap-2 rounded-[10px] py-2.5 pl-1 pr-3.5 sm:py-3 sm:pl-1 sm:pr-3.5', isDisabled ? 'bg-border' : 'bg-secondary')}>
						<span
							aria-hidden
							className={cn(
								'h-5 w-0.5 shrink-0 rounded-full transition-[background-color,box-shadow]',
								isDisabled ? 'bg-subtle-foreground' : isError ? 'bg-destructive' : isFocused ? 'bg-white' : 'bg-border group-hover:bg-white',
							)}
						/>
						{showIcon ? (
							<span
								className={cn(
									'flex shrink-0 items-center [&_svg]:h-3 [&_svg]:w-3 sm:[&_svg]:h-6 sm:[&_svg]:w-6',
									isDisabled ? 'text-subtle-foreground' : isError ? 'text-destructive' : 'text-subtle-foreground',
								)}
							>
								{icon}
							</span>
						) : null}
						<input
							type={type}
							disabled={disabled}
							onFocus={(event) => {
								setIsFocused(true)
								onFocus?.(event)
							}}
							onBlur={(event) => {
								setIsFocused(false)
								onBlur?.(event)
							}}
							className={cn(
								'w-full min-w-0 bg-transparent text-sm font-medium tracking-[-0.21px] text-foreground placeholder:text-subtle-foreground focus:outline-none disabled:cursor-not-allowed disabled:text-subtle-foreground disabled:placeholder:text-subtle-foreground sm:text-base sm:tracking-[-0.24px]',
								isDisabled
									? '[&:-webkit-autofill]:[-webkit-text-fill-color:hsl(var(--subtle-foreground))] [&:-webkit-autofill]:[box-shadow:0_0_0_1000px_hsl(var(--border))_inset]'
									: '[&:-webkit-autofill:focus]:[-webkit-text-fill-color:hsl(var(--foreground))] [&:-webkit-autofill:focus]:[box-shadow:0_0_0_1000px_hsl(var(--secondary))_inset] [&:-webkit-autofill:hover]:[-webkit-text-fill-color:hsl(var(--foreground))] [&:-webkit-autofill:hover]:[box-shadow:0_0_0_1000px_hsl(var(--secondary))_inset] [&:-webkit-autofill]:caret-foreground [&:-webkit-autofill]:[-webkit-text-fill-color:hsl(var(--foreground))] [&:-webkit-autofill]:[box-shadow:0_0_0_1000px_hsl(var(--secondary))_inset] [&:-webkit-autofill]:[transition:background-color_9999s_ease-in-out_0s]',
								className,
							)}
							ref={ref}
							{...props}
						/>
					</div>
				</div>
			</div>
		)
	},
)
Input.displayName = 'Input'

export { Input }
